import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '@/domains/auth/services/auth.service';
import {
  fetchDriverProfile,
  fetchDriverDeliveries,
  setAvailability,
  fetchDriverStats,
  acceptDelivery,
  startDelivery,
  completeDelivery,
} from '@/domains/driver/services/driver.service';
import { getErrorMessage } from '@/utils/apiErrorHandler';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  PremiumDashboardLayout,
  StatCard,
  EmptyState,
  LoadingSpinner,
} from '@/components/dashboard/PremiumDashboardLayout';
import type { Delivery, DriverProfile, DriverStats } from '@/domains/driver/services/driver.service';

const TABS = [
  { id: 'accueil', label: 'Accueil', icon: 'ri-home-line' },
  { id: 'missions', label: 'Missions', icon: 'ri-truck-line' },
  { id: 'revenus', label: 'Revenus', icon: 'ri-wallet-3-line' },
  { id: 'zones', label: 'Zones', icon: 'ri-map-pin-line' },
  { id: 'profil', label: 'Profil', icon: 'ri-user-line' },
];

const PAGE_TITLES: Record<string, string> = {
  accueil: 'Tableau de bord livreur',
  missions: 'Gestion des missions',
  revenus: 'Suivi des revenus',
  zones: 'Zones de couverture',
  profil: 'Mon profil livreur',
};

export default function LivreurDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('accueil');
  const [error, setError] = useState('');
  const [isAvailable, setIsAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [driver, setDriver] = useState<DriverProfile | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [stats, setStats] = useState<DriverStats | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [profile, deliveryList, statsData] = await Promise.all([
        fetchDriverProfile(),
        fetchDriverDeliveries(),
        fetchDriverStats(),
      ]);
      setDriver(profile);
      setDeliveries(deliveryList);
      setStats(statsData);
      setError('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!getSession()) {
      navigate('/login');
      return;
    }
    loadData();
  }, [navigate, loadData]);

  const handleAvailabilityToggle = async () => {
    try {
      await setAvailability(!isAvailable);
      setIsAvailable(!isAvailable);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDeliveryAction = async (deliveryId: number, action: 'accept' | 'start' | 'complete') => {
    setActionLoading(deliveryId);
    try {
      if (action === 'accept') await acceptDelivery(deliveryId);
      else if (action === 'start') await startDelivery(deliveryId);
      else await completeDelivery(deliveryId);
      await loadData();
    } catch {
      setDeliveries((prev) =>
        prev.map((d) => {
          if (d.id !== deliveryId) return d;
          const next =
            action === 'accept' ? 'en_transit' : action === 'start' ? 'en_transit' : 'livré';
          return { ...d, statut: next };
        }),
      );
    } finally {
      setActionLoading(null);
    }
  };

  const pendingMissions = deliveries.filter((d) => d.statut === 'en_attente');
  const activeMissions = deliveries.filter((d) => d.statut === 'en_transit');
  const completedMissions = deliveries.filter((d) => d.statut === 'livré');

  return (
    <PremiumDashboardLayout
      tabs={TABS.map((t) => (t.id === 'missions' ? { ...t, badge: pendingMissions.length } : t))}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      pageTitle={PAGE_TITLES[activeTab] ?? 'Dashboard Livreur'}
      roleLabel="Livreur Communautaire"
      accent="orange"
      userName={driver ? `${driver.firstnames} ${driver.lastname}` : undefined}
      userZone={driver?.zone?.name}
      userInitial={driver?.firstnames?.[0]}
      mobileNavTabIds={['accueil', 'missions', 'revenus', 'profil']}
    >
      {error && <ErrorAlert error={error} onClose={() => setError('')} />}

      {activeTab === 'accueil' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 rounded-2xl p-6 text-white border border-slate-800">
            <span className="bg-orange-500/10 text-orange-400 border border-orange-500/20 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider">
              Livraison Communautaire
            </span>
            <h2 className="text-base font-bold tracking-tight mt-2">Bonjour, {driver?.firstnames ?? 'Livreur'} !</h2>
            <p className="text-xs text-slate-400 mt-1">
              Acceptez des missions ponctuelles dans votre quartier et renforcez les liens communautaires.
            </p>
          </div>

          <AvailabilityToggle isAvailable={isAvailable} onToggle={handleAvailabilityToggle} />

          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon="ri-truck-line" label="En cours" value={activeMissions.length} color="orange" />
                <StatCard icon="ri-time-line" label="En attente" value={pendingMissions.length} color="amber" />
                <StatCard icon="ri-check-double-line" label="Complétées" value={stats?.completed_deliveries ?? 0} color="emerald" />
                <StatCard icon="ri-star-line" label="Note moyenne" value={stats?.average_rating?.toFixed(1) ?? '—'} color="blue" />
              </div>

              <section>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Missions en cours</h3>
                {activeMissions.length === 0 ? (
                  <EmptyState icon="ri-truck-line" message="Aucune livraison en cours pour le moment" />
                ) : (
                  <div className="space-y-3">
                    {activeMissions.map((d) => (
                      <MissionCard
                        key={d.id}
                        delivery={d}
                        actionLoading={actionLoading}
                        onAction={handleDeliveryAction}
                      />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}

      {activeTab === 'missions' && (
        <div className="space-y-6">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              {pendingMissions.length > 0 && (
                <section>
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">
                    Nouvelles missions ({pendingMissions.length})
                  </h3>
                  <div className="space-y-3">
                    {pendingMissions.map((d) => (
                      <MissionCard
                        key={d.id}
                        delivery={d}
                        actionLoading={actionLoading}
                        onAction={handleDeliveryAction}
                      />
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">En cours</h3>
                {activeMissions.length === 0 ? (
                  <EmptyState icon="ri-route-line" message="Aucune mission active" />
                ) : (
                  <div className="space-y-3">
                    {activeMissions.map((d) => (
                      <MissionCard
                        key={d.id}
                        delivery={d}
                        actionLoading={actionLoading}
                        onAction={handleDeliveryAction}
                      />
                    ))}
                  </div>
                )}
              </section>

              <section>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-3">Terminées</h3>
                {completedMissions.length === 0 ? (
                  <EmptyState icon="ri-check-line" message="Aucune mission terminée" />
                ) : (
                  <div className="space-y-3">
                    {completedMissions.map((d) => (
                      <MissionCard key={d.id} delivery={d} actionLoading={actionLoading} onAction={handleDeliveryAction} />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      )}

      {activeTab === 'revenus' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatCard icon="ri-sun-line" label="Aujourd'hui" value={`${(stats?.today_earnings ?? 0).toLocaleString()} GNF`} color="orange" />
            <StatCard icon="ri-calendar-line" label="Cette semaine" value={`${(stats?.week_earnings ?? 0).toLocaleString()} GNF`} color="blue" />
            <StatCard icon="ri-calendar-check-line" label="Ce mois" value={`${(stats?.month_earnings ?? 0).toLocaleString()} GNF`} color="emerald" />
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Récapitulatif des gains</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-slate-50">
                <span className="text-slate-500">Total livraisons</span>
                <span className="font-semibold">{stats?.total_deliveries ?? 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-50">
                <span className="text-slate-500">Livraisons complétées</span>
                <span className="font-semibold text-emerald-600">{stats?.completed_deliveries ?? 0}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-slate-50">
                <span className="text-slate-500">Annulées</span>
                <span className="font-semibold text-rose-600">{stats?.cancelled_deliveries ?? 0}</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-slate-500 font-medium">Revenus cumulés</span>
                <span className="font-bold text-orange-600">{(stats?.total_earnings ?? 0).toLocaleString()} GNF</span>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 text-xs text-amber-800">
            <i className="ri-information-line mr-1" />
            Les paiements Mobile Money (Orange Money, MTN MoMo) seront versés sur votre compte enregistré.
          </div>
        </div>
      )}

      {activeTab === 'zones' && (
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Zone principale</h3>
            <p className="text-sm text-slate-600">
              <i className="ri-map-pin-2-line text-orange-500 mr-2" />
              {driver?.zone?.name ?? 'Non définie'}
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Zones de couverture</h3>
            {(driver?.coverageZones?.length ?? 0) === 0 ? (
              <EmptyState icon="ri-map-2-line" message="Aucune zone de couverture configurée" />
            ) : (
              <div className="flex flex-wrap gap-2">
                {driver?.coverageZones?.map((z) => (
                  <span key={z.id} className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-xl text-xs font-semibold border border-orange-100">
                    {z.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
            <h3 className="text-sm font-bold text-slate-800 mb-4">Créneaux de disponibilité</h3>
            {(driver?.availabilitySlots?.length ?? 0) === 0 ? (
              <EmptyState icon="ri-time-line" message="Aucun créneau configuré" />
            ) : (
              <div className="space-y-2">
                {driver?.availabilitySlots?.map((slot) => (
                  <div key={slot.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl text-sm">
                    <span className="font-medium text-slate-700">{slot.name}</span>
                    {slot.start_time && slot.end_time && (
                      <span className="text-xs text-slate-400">{slot.start_time} – {slot.end_time}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {driver?.specializations && driver.specializations.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
              <h3 className="text-sm font-bold text-slate-800 mb-4">Spécialisations</h3>
              <div className="flex flex-wrap gap-2">
                {driver.specializations.map((s) => (
                  <span key={s.id} className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-xl text-xs font-semibold">
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'profil' && driver && (
        <ProfileSection driver={driver} />
      )}
    </PremiumDashboardLayout>
  );
}

function AvailabilityToggle({ isAvailable, onToggle }: { isAvailable: boolean; onToggle: () => void }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex items-center justify-between">
      <div className="flex items-center space-x-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${isAvailable ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
          <i className={isAvailable ? 'ri-checkbox-circle-line' : 'ri-pause-circle-line'} />
        </div>
        <div>
          <h3 className="text-sm font-bold text-slate-800">Disponibilité</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {isAvailable ? 'Vous recevez des missions' : 'Vous ne recevez pas de missions'}
          </p>
        </div>
      </div>
      <button
        onClick={onToggle}
        className={`px-5 py-2.5 rounded-xl text-xs font-bold transition-colors ${
          isAvailable ? 'bg-emerald-600 text-white hover:bg-emerald-500' : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
        }`}
      >
        {isAvailable ? 'Disponible' : 'Indisponible'}
      </button>
    </div>
  );
}

function MissionCard({
  delivery,
  actionLoading,
  onAction,
}: {
  delivery: Delivery;
  actionLoading: number | null;
  onAction: (id: number, action: 'accept' | 'start' | 'complete') => void;
}) {
  const cmd = delivery.commande;
  const isLoading = actionLoading === delivery.id;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="text-xs font-bold text-orange-600">{cmd?.code_suivi}</p>
          <p className="text-sm font-semibold text-slate-800 mt-1">{cmd?.adresse_livraison}</p>
        </div>
        <StatusBadge statut={delivery.statut} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs text-slate-500 mb-4">
        <div>
          <i className="ri-map-pin-line mr-1" />
          Départ : {cmd?.adresse_ramassage}
        </div>
        {cmd?.distance_km && (
          <div>
            <i className="ri-route-line mr-1" />
            {cmd.distance_km} km
          </div>
        )}
        {cmd?.prix_estime && (
          <div>
            <i className="ri-money-dollar-circle-line mr-1" />
            {cmd.prix_estime.toLocaleString()} GNF
          </div>
        )}
        {cmd?.client && (
          <div>
            <i className="ri-user-line mr-1" />
            {cmd.client.firstnames} {cmd.client.lastname}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {delivery.statut === 'en_attente' && (
          <button
            disabled={isLoading}
            onClick={() => onAction(delivery.id, 'accept')}
            className="flex-1 py-2 bg-orange-500 hover:bg-orange-400 text-white text-xs font-bold rounded-xl disabled:opacity-50"
          >
            {isLoading ? '...' : 'Accepter'}
          </button>
        )}
        {delivery.statut === 'en_transit' && (
          <button
            disabled={isLoading}
            onClick={() => onAction(delivery.id, 'complete')}
            className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl disabled:opacity-50"
          >
            {isLoading ? '...' : 'Marquer livré'}
          </button>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ statut }: { statut: string }) {
  const styles: Record<string, string> = {
    en_attente: 'bg-amber-50 text-amber-700 border-amber-100',
    en_transit: 'bg-blue-50 text-blue-700 border-blue-100',
    livré: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${styles[statut] ?? 'bg-slate-50 text-slate-600'}`}>
      {statut.replace('_', ' ')}
    </span>
  );
}

function ProfileSection({ driver }: { driver: DriverProfile }) {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
        <h3 className="text-sm font-bold text-slate-800 mb-4">Informations personnelles</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <InfoField label="Nom" value={`${driver.firstnames} ${driver.lastname}`} />
          <InfoField label="Téléphone" value={driver.phone} />
          <InfoField label="Email" value={driver.email ?? '—'} />
          <InfoField label="Statut" value={driver.status} />
        </div>
      </div>

      {driver.vehicleCapacity && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-4">Véhicule</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <InfoField label="Type" value={driver.vehicleCapacity.vehicleType.name} />
            <InfoField label="Poids max" value={`${driver.vehicleCapacity.max_weight} kg`} />
            <InfoField label="Volume max" value={`${driver.vehicleCapacity.max_volume} m³`} />
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="font-medium text-slate-800 mt-0.5 capitalize">{value}</p>
    </div>
  );
}
